import type { Theme } from '@emotion/react';
import { css } from '@emotion/react';
import { Box } from '@resi-media/resi-ui';
import { match } from 'ts-pattern';
import { NotFoundSection } from '@sites/components/NotFoundSection';
import { PlayerScript } from '@sites/components/PlayerScript';
import { PAGE_TYPES } from '@sites/constants/generic';
import { useLivePlayer } from '@sites/contexts/livePlayerContext';
import { PlayerContextProvider } from '@sites/contexts/playerContext';
import type { Playlists, Shared, Videos } from '@sites/types';
import { LivePlayerCountdown } from '../LivePlayerCountdown';
import { LivePlayerUnavailable } from '../LivePlayerUnavailable';

const S = {
  container: (theme: Theme, isNotFound: boolean) =>
    css({
      background: theme.palette.activeMode === 'light' && isNotFound ? 'white' : 'black',
      borderRadius: '12px',
      boxShadow: `0px 2px 2px 0px ${theme.palette.common.black}15,
              0px 0px 2px 0px ${theme.palette.common.black}15`,
      position: 'relative',
      overflow: 'hidden',
      width: '100%',
      aspectRatio: '16 / 9',
      [theme.mq.lg]: {
        maxWidth: `min(100vw, calc((100vh - ${theme.spacing.x60}) * 16 / 9))`,
        margin: '0 auto',
      },
    }),
};

type _Props = {
  isLoading: boolean;
  variant?: Shared.Video.Variants;
  video?: Playlists.Derived.Video | Videos.Get.Video;
  webChannelId?: string;
};

const VideoPlayerSection = ({ isLoading, variant = PAGE_TYPES.DEFAULT, video: currentVideo, webChannelId }: _Props) => {
  const { isNotFound, showLiveCountdown, showLiveVideo } = useLivePlayer();
  const isOnDemandVideoNotFound = !isLoading && !currentVideo;

  return (
    <div
      css={(theme) =>
        css({
          width: '100%',
          [theme.mq.lg]: {
            height: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            backgroundColor: theme.palette.contrastInverse,
          },
        })
      }
    >
      {match({
        isDefaultOrSearchPage: variant === PAGE_TYPES.DEFAULT || variant === PAGE_TYPES.SEARCH,
        isLivePage: variant === PAGE_TYPES.LIVE,
      })
        .with({ isDefaultOrSearchPage: true }, () => (
          <Box css={(theme) => S.container(theme, isOnDemandVideoNotFound)} data-testid="video-player-section">
            {currentVideo ? (
              <PlayerContextProvider>
                <PlayerScript video={currentVideo} />
              </PlayerContextProvider>
            ) : (
              <NotFoundSection
                css={css({ display: isOnDemandVideoNotFound ? 'flex' : 'none' })}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
              />
            )}
          </Box>
        ))
        .with({ isLivePage: true }, () => (
          <Box css={(theme) => S.container(theme, isNotFound)}>
            {match({ isLoading, showLiveCountdown, showLiveVideo })
              .with({ showLiveVideo: true }, () => (
                <Box css={(theme) => S.container(theme, isNotFound)} dataTestId="live-video-player-section">
                  <PlayerContextProvider>
                    <PlayerScript
                      channelId={webChannelId}
                      isAutoPlay={true}
                      mode="live"
                      type={PlayerScript.EMBED_TYPE.WEB_CHANNEL}
                    />
                  </PlayerContextProvider>
                </Box>
              ))
              .with({ showLiveCountdown: true }, () => (
                <LivePlayerCountdown customCss={css(`max-width: none; max-height: none;`)} />
              ))
              .with({ isLoading: false, showLiveCountdown: false, showLiveVideo: false }, () => (
                <LivePlayerUnavailable customCss={css(`max-width: none; max-height: none;`)} />
              ))
              .otherwise(() => null)}
          </Box>
        ))
        .otherwise(() => null)}
    </div>
  );
};

VideoPlayerSection.displayName = 'VideoPlayerSection';

export default VideoPlayerSection;
